import { m, wrap, AnimatePresence, useWillChange } from 'framer-motion';
import { HTMLAttributes, forwardRef } from 'react';
import classNames from 'classnames';
import ArrowSVG from '@/svg/arrow.svg?url';
import { easeOutQuart } from '@/utils/easings';

export interface Props extends HTMLAttributes<HTMLElement> {
    /**
     * Номер страницы
     */
    page: number;
    /**
     * Количество страниц
     */
    length: number;
    /**
     * Направление анимации
     */
    direction?: 'prev' | 'next';
    onPaginationChange?: (number: number) => void;
}

const fromRightVariant = {
    clipPath: 'inset(0% 0% 0% 100%)',
    x: '-75%',
    y: '-50%',
};

const fromLeftVariant = {
    clipPath: 'inset(0% 100% 0% 0%)',
    x: '-25%',
    y: '-50%',
};

const VISIBLE_LINES = 5;

const smallToRightVariant = {
    x: 8,
    scaleY: 1,
};

const smallToLeftVariant = {
    x: -8,
    scaleY: 1,
};

const bigToRightVariant = {
    x: 8,
    scaleY: 1.75,
};

const bigToLeftVariant = {
    x: -8,
    scaleY: 1.75,
};

const leftDict: Record<number, Record<string, Record<string, number>>> = {
    0: {
        prev: smallToLeftVariant,
        next: smallToRightVariant,
    },
    1: {
        prev: smallToLeftVariant,
        next: smallToRightVariant,
    },
    2: {
        prev: smallToLeftVariant,
        next: bigToRightVariant,
    },
    3: {
        prev: bigToLeftVariant,
        next: smallToRightVariant,
    },
    4: {
        prev: smallToLeftVariant,
        next: smallToRightVariant,
    },
};

const rightDict: Record<number, Record<string, Record<string, number>>> = {
    0: {
        next: smallToRightVariant,
        prev: smallToLeftVariant,
    },
    1: {
        next: bigToRightVariant,
        prev: smallToLeftVariant,
    },
    2: {
        next: smallToRightVariant,
        prev: bigToLeftVariant,
    },
    3: {
        next: smallToRightVariant,
        prev: smallToLeftVariant,
    },
    4: {
        next: smallToRightVariant,
        prev: smallToLeftVariant,
    },
};

const Pagination = forwardRef<HTMLDivElement, Props>(
    ({ page = 1, direction = 'next', length, onPaginationChange, ...props }, ref) => {
        const current = page;
        const willChange = useWillChange();
        const lines = new Array(VISIBLE_LINES).fill('');

        return (
            <div {...props} ref={ref} className={classNames(`pagination`, props.className)}>
                <button
                    className="pagination-btn"
                    onClick={() => {
                        const number = wrap(1, length + 1, current - 1);
                        onPaginationChange?.(number);
                    }}
                    aria-label="Назад"
                >
                    <span className="pagination-btn__icon">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={ArrowSVG} alt="" width={12} height={12} className="img-fluid" decoding="async" />
                    </span>
                </button>

                <div className="pagination-center">
                    <div className="pagination-lines pagination-lines--prev">
                        {lines.map((_, i) => {
                            return (
                                <m.div
                                    key={`${i}${current}`}
                                    className="pagination-line"
                                    transition={{
                                        duration: 0.6,
                                        ease: easeOutQuart,
                                    }}
                                    animate={leftDict[i][direction]}
                                />
                            );
                        })}
                    </div>
                    <div className="pagination-number">
                        <div className="pagination-hat pagination-hat--top" />

                        <AnimatePresence mode="wait" initial={false}>
                            <m.div
                                key={current}
                                style={{ willChange }}
                                transition={{
                                    duration: 0.3,
                                    ease: easeOutQuart,
                                }}
                                className="pagination-number__inner"
                                initial={direction === 'next' ? fromRightVariant : fromLeftVariant}
                                animate={{
                                    clipPath: 'inset(0% 0% 0% 0%)',
                                    x: '-50%',
                                    y: '-50%',
                                }}
                                exit={direction === 'next' ? fromLeftVariant : fromRightVariant}
                            >
                                {current}
                            </m.div>
                        </AnimatePresence>

                        <div className="pagination-hat pagination-hat--bottom" />
                    </div>
                    <div className="pagination-lines pagination-lines--next">
                        {lines.map((_, i) => {
                            return (
                                <m.div
                                    key={`${i}${current}`}
                                    className="pagination-line"
                                    transition={{
                                        duration: 0.6,
                                        ease: easeOutQuart,
                                    }}
                                    animate={rightDict[i][direction]}
                                />
                            );
                        })}
                    </div>
                </div>

                <button
                    className="pagination-btn pagination-btn--next"
                    onClick={() => {
                        const number = wrap(1, length + 1, current + 1);
                        onPaginationChange?.(number);
                    }}
                    aria-label="Вперед"
                >
                    <span className="pagination-btn__icon">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={ArrowSVG} alt="" width={12} height={12} className="img-fluid" decoding="async" />
                    </span>
                </button>
            </div>
        );
    },
);

Pagination.displayName = 'Pagination';

export default Pagination;
